// src/lib/advisorQuoteGenerator.js
// §7bis Step B — Aperçu PDF du devis Advisor. Document commercial (charte ARTKIV),
// PAS une facture électronique réglementaire : le numéro D-AAAA-xxxx est un
// brouillon interne, la facture définitive est émise par l'outil de l'Advisor.
// jsPDF + html2canvas (même pipeline que museumProposalGenerator). i18n FR/EN/DE.
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const C = { ink: "#1A1A1A", gray: "#6B6B6B", gold: "#C9A961", goldDark: "#8B7355", stone: "#E5E3DF", cream: "#FAFAF8" };

const COPY = {
  fr: {
    quote: "DEVIS", number: "N° interne", date: "Date", validUntil: "Valable jusqu’au",
    from: "Émetteur", to: "Client", object: "Objet",
    colDesignation: "Désignation", colUnit: "Unité", colQty: "Qté", colPrice: "PU HT",
    colDiscount: "Remise", colVat: "TVA", colTotal: "Total HT",
    units: { flat: "forfait", day: "jour", hour: "heure", artwork: "œuvre", percentage: "%", other: "—" },
    expense: "Débours",
    subtotal: "Sous-total HT", globalDiscount: "Remise globale", afterDiscount: "HT après remise",
    tax: "TVA", total: "TOTAL TTC", deposit: "Acompte à la commande",
    delivery: "Délai / réalisation", payment: "Conditions de règlement",
    notes: "Notes", exclusions: "Exclusions",
    siren: "SIREN/SIRET", vat: "TVA", poRef: "Réf. commande", email: "Email",
    statusDraft: "Brouillon",
    legal: "Ce document est un devis commercial. Il ne constitue pas une facture électronique réglementaire ; la facture définitive et sa numérotation comptable sont émises par l’outil de facturation de l’émetteur. Devis établi via ARTKIV.",
    zones: { fr: "France", eu: "Union européenne", non_eu: "Hors UE" },
  },
  en: {
    quote: "QUOTE", number: "Internal no.", date: "Date", validUntil: "Valid until",
    from: "Issuer", to: "Client", object: "Object",
    colDesignation: "Description", colUnit: "Unit", colQty: "Qty", colPrice: "Unit price",
    colDiscount: "Disc.", colVat: "VAT", colTotal: "Total excl.",
    units: { flat: "flat", day: "day", hour: "hour", artwork: "artwork", percentage: "%", other: "—" },
    expense: "Expense",
    subtotal: "Subtotal excl.", globalDiscount: "Global discount", afterDiscount: "After discount",
    tax: "VAT", total: "TOTAL INCL.", deposit: "Deposit on order",
    delivery: "Lead time / delivery", payment: "Payment terms",
    notes: "Notes", exclusions: "Exclusions",
    siren: "Reg. no.", vat: "VAT", poRef: "PO ref.", email: "Email",
    statusDraft: "Draft",
    legal: "This document is a commercial quote. It is not a regulatory electronic invoice; the final invoice and its accounting numbering are issued by the issuer’s billing tool. Quote produced via ARTKIV.",
    zones: { fr: "France", eu: "European Union", non_eu: "Outside EU" },
  },
  de: {
    quote: "ANGEBOT", number: "Interne Nr.", date: "Datum", validUntil: "Gültig bis",
    from: "Aussteller", to: "Kunde", object: "Gegenstand",
    colDesignation: "Bezeichnung", colUnit: "Einheit", colQty: "Menge", colPrice: "EP netto",
    colDiscount: "Rabatt", colVat: "USt", colTotal: "Netto",
    units: { flat: "Pauschal", day: "Tag", hour: "Std.", artwork: "Werk", percentage: "%", other: "—" },
    expense: "Auslage",
    subtotal: "Zwischensumme netto", globalDiscount: "Gesamtrabatt", afterDiscount: "Nach Rabatt",
    tax: "USt", total: "GESAMT BRUTTO", deposit: "Anzahlung bei Auftrag",
    delivery: "Frist / Lieferung", payment: "Zahlungsbedingungen",
    notes: "Notizen", exclusions: "Ausschlüsse",
    siren: "Reg.-Nr.", vat: "USt", poRef: "Bestellnr.", email: "E-Mail",
    statusDraft: "Entwurf",
    legal: "Dieses Dokument ist ein kommerzielles Angebot. Es ist keine gesetzliche elektronische Rechnung; die endgültige Rechnung und deren Nummerierung erstellt das Abrechnungstool des Ausstellers. Angebot erstellt über ARTKIV.",
    zones: { fr: "Frankreich", eu: "Europäische Union", non_eu: "Außerhalb der EU" },
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;
const locOf = (l) => (String(l || "fr").slice(0, 2) === "de" ? "de-DE" : String(l || "fr").slice(0, 2) === "en" ? "en-GB" : "fr-FR");
const num = (v) => (Number(v) || 0);
const money = (n, l) => num(n).toLocaleString(locOf(l), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const fmtDate = (d, l) => (d ? new Date(d).toLocaleDateString(locOf(l)) : "—");

function advisorBlock(a = {}) {
  if (!a) return "";
  const lines = [
    a.legal_name || a.trade_name,
    a.legal_form,
    [a.address_line1, [a.postal_code, a.city].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    a.country_code && a.country_code !== "FR" ? a.country_code : null,
    a.siret ? `SIRET ${a.siret}` : a.siren ? `SIREN ${a.siren}` : null,
    a.vat_number ? `TVA ${a.vat_number}` : null,
    a.billing_email,
  ].filter(Boolean);
  return lines.map((l) => `<div>${esc(l)}</div>`).join("");
}

function clientBlock(c = {}, tx) {
  const lines = [
    c.name,
    c.address,
    c.zone && c.zone !== "fr" ? (tx.zones[c.zone] || c.zone) : null,
    c.siren ? `${tx.siren} ${c.siren}` : null,
    c.vat ? `${tx.vat} ${c.vat}` : null,
    c.email ? `${tx.email} ${c.email}` : null,
    c.po_ref ? `${tx.poRef} ${c.po_ref}` : null,
  ].filter(Boolean);
  return lines.map((l) => `<div>${esc(l)}</div>`).join("") || "<div>—</div>";
}

function quoteHtml(data) {
  const { internalNumber, object, advisor, client, lines = [], totals = {},
    discountAmount, deposit, validUntil, delivery, payment, notes, exclusions, lang } = data;
  const tx = pick(lang);
  const rows = lines.map((l) => {
    const net = num(l.qty) * num(l.unit_price_ex_tax) * (1 - num(l.discount_pct) / 100);
    return `<tr>
      <td style="padding:7px 8px;border-bottom:1px solid ${C.stone};">
        ${esc(l.label) || "—"}${l.kind === "expense" ? ` <span style="font-size:10px;color:${C.goldDark};text-transform:uppercase;">${tx.expense}</span>` : ""}
        ${l.detail ? `<div style="font-size:11px;color:${C.gray};">${esc(l.detail)}</div>` : ""}
      </td>
      <td style="padding:7px 8px;border-bottom:1px solid ${C.stone};color:${C.gray};">${tx.units[l.unit] || l.unit || "—"}</td>
      <td style="padding:7px 8px;border-bottom:1px solid ${C.stone};text-align:right;">${num(l.qty)}</td>
      <td style="padding:7px 8px;border-bottom:1px solid ${C.stone};text-align:right;">${money(l.unit_price_ex_tax, lang)}</td>
      <td style="padding:7px 8px;border-bottom:1px solid ${C.stone};text-align:right;">${num(l.discount_pct) ? num(l.discount_pct) + " %" : "—"}</td>
      <td style="padding:7px 8px;border-bottom:1px solid ${C.stone};text-align:right;">${num(l.vat_rate)} %</td>
      <td style="padding:7px 8px;border-bottom:1px solid ${C.stone};text-align:right;font-weight:600;">${money(net, lang)}</td>
    </tr>`;
  }).join("");

  const cond = [
    validUntil ? `<div><strong>${tx.validUntil} :</strong> ${fmtDate(validUntil, lang)}</div>` : "",
    delivery ? `<div><strong>${tx.delivery} :</strong> ${esc(delivery)}</div>` : "",
    payment ? `<div><strong>${tx.payment} :</strong> ${esc(payment)}</div>` : "",
  ].filter(Boolean).join("");

  const extra = [
    notes ? `<div style="margin-top:8px;"><strong>${tx.notes} :</strong> ${esc(notes)}</div>` : "",
    exclusions ? `<div style="margin-top:4px;"><strong>${tx.exclusions} :</strong> ${esc(exclusions)}</div>` : "",
  ].filter(Boolean).join("");

  const exemption = advisor?.vat_exemption_mention && num(totals.tax) === 0
    ? `<div style="font-size:11px;color:${C.gray};margin-top:4px;">${esc(advisor.vat_exemption_mention)}</div>` : "";

  return `
  <div class="quote-container" style="width:800px;background:#fff;color:${C.ink};font-family:'Helvetica Neue',Arial,sans-serif;font-size:13px;line-height:1.5;padding:8px;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid ${C.ink};padding-bottom:14px;margin-bottom:18px;">
      <div>
        <div style="font-family:Georgia,serif;font-size:26px;letter-spacing:0.12em;">ARTKIV</div>
        <div style="font-family:Georgia,serif;font-size:20px;color:${C.goldDark};margin-top:4px;">${tx.quote}</div>
      </div>
      <div style="text-align:right;font-size:12px;color:${C.gray};">
        <div><strong style="color:${C.ink};">${tx.number} :</strong> ${esc(internalNumber) || tx.statusDraft}</div>
        <div><strong style="color:${C.ink};">${tx.date} :</strong> ${fmtDate(new Date(), lang)}</div>
        ${validUntil ? `<div><strong style="color:${C.ink};">${tx.validUntil} :</strong> ${fmtDate(validUntil, lang)}</div>` : ""}
      </div>
    </div>

    <div style="display:flex;gap:24px;margin-bottom:18px;">
      <div style="flex:1;">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:${C.goldDark};margin-bottom:6px;">${tx.from}</div>
        <div style="font-size:12.5px;color:${C.ink};">${advisorBlock(advisor) || "—"}</div>
      </div>
      <div style="flex:1;">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:${C.goldDark};margin-bottom:6px;">${tx.to}</div>
        <div style="font-size:12.5px;color:${C.ink};">${clientBlock(client, tx)}</div>
      </div>
    </div>

    ${object ? `<div style="margin-bottom:16px;padding:10px 12px;background:${C.cream};border-left:3px solid ${C.gold};"><strong>${tx.object} :</strong> ${esc(object)}</div>` : ""}

    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
      <thead>
        <tr style="background:${C.cream};">
          <th style="padding:8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:${C.gray};">${tx.colDesignation}</th>
          <th style="padding:8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:${C.gray};">${tx.colUnit}</th>
          <th style="padding:8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:${C.gray};">${tx.colQty}</th>
          <th style="padding:8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:${C.gray};">${tx.colPrice}</th>
          <th style="padding:8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:${C.gray};">${tx.colDiscount}</th>
          <th style="padding:8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:${C.gray};">${tx.colVat}</th>
          <th style="padding:8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:${C.gray};">${tx.colTotal}</th>
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="7" style="padding:12px;color:${C.gray};">—</td></tr>`}</tbody>
    </table>

    <div style="display:flex;justify-content:flex-end;margin-bottom:18px;">
      <div style="width:300px;font-size:13px;">
        <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:${C.gray};">${tx.subtotal}</span><span>${money(totals.baseHT, lang)}</span></div>
        ${num(discountAmount) > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:${C.gray};">${tx.globalDiscount}</span><span>− ${money(discountAmount, lang)}</span></div>
        <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:${C.gray};">${tx.afterDiscount}</span><span>${money(totals.afterDiscount, lang)}</span></div>` : ""}
        <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:${C.gray};">${tx.tax}</span><span>${money(totals.tax, lang)}</span></div>
        ${exemption}
        <div style="display:flex;justify-content:space-between;padding:8px 0;margin-top:4px;border-top:2px solid ${C.ink};font-weight:700;font-size:15px;"><span>${tx.total}</span><span>${money(totals.total, lang)}</span></div>
        ${num(deposit) > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:${C.goldDark};"><span>${tx.deposit}</span><span>${money(deposit, lang)}</span></div>` : ""}
      </div>
    </div>

    ${cond ? `<div style="font-size:12px;color:${C.ink};margin-bottom:8px;line-height:1.7;">${cond}</div>` : ""}
    ${extra ? `<div style="font-size:12px;color:${C.ink};margin-bottom:8px;line-height:1.6;">${extra}</div>` : ""}

    <div style="margin-top:22px;padding-top:12px;border-top:1px solid ${C.stone};font-size:10.5px;color:${C.gray};line-height:1.5;">${tx.legal}</div>
  </div>`;
}

export async function downloadAdvisorQuotePdf(data) {
  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = "-9999px";
  el.style.top = "0";
  el.innerHTML = quoteHtml(data);
  document.body.appendChild(el);
  try {
    const canvas = await html2canvas(el.firstElementChild, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
    const img = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const w = 210, pageH = 297;
    const h = (canvas.height * w) / canvas.width;
    let left = h, pos = 0;
    pdf.addImage(img, "JPEG", 0, pos, w, h);
    left -= pageH;
    while (left > 0) {
      pos = left - h;
      pdf.addPage();
      pdf.addImage(img, "JPEG", 0, pos, w, h);
      left -= pageH;
    }
    const name = `${data.internalNumber || "devis"}.pdf`.replace(/[^\w.-]+/g, "_");
    pdf.save(name);
    return true;
  } catch (e) {
    console.error("[advisorQuoteGenerator] failed", e);
    return false;
  } finally {
    document.body.removeChild(el);
  }
}
